import type { PostHog } from 'posthog-js';
import type { Info, Me } from './types';

let client: PostHog | undefined;
let starting: Promise<void> | undefined;
const tabs = new Set(['status','connections','context','skills','team','billing','audit','profile']);
/** A shared skill's id is the only key to an unlisted link, so analytics sees "/s/" alone. */
export const redactPath = (pathname: string) => pathname.replace(/^\/s\/[^/]+.*$/, '/s/');
function safeUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const tab = u.hash.replace(/^#\/?/, '');
    return u.origin + redactPath(u.pathname) + (tabs.has(tab) ? `#/${tab}` : '');
  } catch { return ''; }
}

// Campaign parameters a public link may carry. Any OTHER query key keeps the
// visit silent, as before: an unknown parameter may be a token.
const campaignKeys = /^(utm_(source|medium|campaign|term|content)|gclid|gbraid|wbraid|msclkid|fbclid|ref)$/;
const searchEngines = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|search\.brave|startpage|qwant|kagi)\.[a-z.]+$/;
const assistants = /(^|\.)(chatgpt\.com|chat\.openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|grok\.com)$/;
function tag(value: string | null | undefined): string | undefined {
  const t = (value ?? '').toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return t || undefined;
}

/**
 * Where a public visit came from, coarse enough to name no person: a site or a
 * campaign label, never a URL, a search query or a click id. Recorded once per
 * browser, so the signup and first task that follow carry the first touch.
 */
export function acquisition(url: URL, referrer: string): Record<string, string> {
  const p = url.searchParams;
  let host = '';
  try { host = referrer ? new URL(referrer).hostname.toLowerCase().replace(/^www\./, '') : ''; } catch { /* unparseable: treat as direct */ }
  if (host === url.hostname.replace(/^www\./, '')) host = '';
  const clickSource = p.has('gclid') || p.has('gbraid') || p.has('wbraid') ? 'google' : p.has('msclkid') ? 'bing' : p.has('fbclid') ? 'facebook' : undefined;
  const medium = tag(p.get('utm_medium'))
    ?? (clickSource === 'facebook' ? 'social' : clickSource ? 'paid' : undefined)
    ?? (!host ? 'direct' : searchEngines.test(host) ? 'organic' : assistants.test(host) ? 'ai-assistant' : 'referral');
  const out: Record<string, string> = {
    acquisition_source: tag(p.get('utm_source')) ?? clickSource ?? tag(host) ?? 'direct',
    acquisition_medium: medium,
    landing_path: redactPath(url.pathname).slice(0, 120),
  };
  const campaign = tag(p.get('utm_campaign') ?? p.get('ref'));
  if (campaign) out.acquisition_campaign = campaign;
  return out;
}

/** Runtime configuration, so the same image stays silent in dev and local. */
export function startAnalytics(info: Pick<Info, 'env' | 'analytics'>, identifyUser = true): Promise<void> {
  // Public pages accept campaign links; the dashboard accepts no query at all.
  const keys = [...new URLSearchParams(location.search).keys()];
  const campaignOnly = !identifyUser && keys.length > 0 && keys.every(k => campaignKeys.test(k));
  if ((location.search && !campaignOnly) || (location.hash && !tabs.has(location.hash.replace(/^#\/?/, '')))) return Promise.resolve();
  if (!info.analytics || info.env !== 'prod' || location.origin !== info.analytics.origin) return Promise.resolve();
  if (starting) return starting;
  // Read the source before anything else, then drop the campaign query so no
  // later event or replay frame carries a click id.
  const source = identifyUser ? undefined : acquisition(new URL(location.href), typeof document === 'undefined' ? '' : document.referrer);
  if (campaignOnly && typeof history !== 'undefined') history.replaceState(history.state, '', location.pathname + location.hash);
  starting = (async () => {
    const { default: posthog } = await import('posthog-js');
    posthog.init(info.analytics!.token, {
      api_host: 'https://us.i.posthog.com',
      ui_host: 'https://us.posthog.com',
      defaults: '2026-01-30',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: { dom_event_allowlist: ['click', 'submit'] },
      mask_all_text: true,
      mask_all_element_attributes: true,
      save_campaign_params: false,
      save_referrer: false,
      person_profiles: 'identified_only',
      disable_surveys: true,
      capture_exceptions: false,
      enable_recording_console_log: false,
      session_recording: {
        sampleRate: 0.3,
        maskAllInputs: true,
        maskTextSelector: '*',
        blockSelector: 'img, video, canvas, iframe, [data-ph-block]',
        recordHeaders: false,
        recordBody: false,
        recordCrossOriginIframes: false,
        maskNetworkRequestFn: () => null,
      },
      before_send: event => {
        if (!event) return null;
        for (const props of [event.properties, event.properties?.$set, event.properties?.$set_once]) {
          if (!props) continue;
          for (const key of Object.keys(props)) {
            if (/utm_|gclid|fbclid|referrer|initial_referring|pathname.*search/i.test(key)) delete props[key];
            else if (typeof props[key] === 'string' && /url|href/i.test(key)) props[key] = safeUrl(props[key]);
          }
        }
        return event;
      },
    });
    client = posthog;
    // First touch only: a later direct visit must not overwrite how someone
    // found morigraph. Super properties ride on every event, signup included.
    if (source) posthog.register_once(source);
    // Stable account identity only; workspace content and email are not needed
    // for the initial analytics/replay setup.
    if (identifyUser) {
      const me: Me = await fetch('/api/me').then(r => r.json());
      if (me.kind === 'user' && me.id) posthog.identify(me.id);
      else posthog.reset();
    }
    const page = () => posthog.capture('$pageview', { $current_url: safeUrl(location.href), environment: 'prod' });
    page();
    addEventListener('hashchange', page);
  })().catch(() => { starting = undefined; });
  return starting;
}
export function resetAnalytics() { client?.reset(); }
